import React from "react";
import { Card } from "../index";

const ContentAnalyticsSkeleton: React.FC = () => {
  return (
    <div className="w-full flex flex-col gap-2">
      {/* Grid Principal: Composição 8/4 Otimizada em Densidade e Presença Visual */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-stretch">
        {/* Coluna Esquerda: Banner + Orientação de Mercado */}
        <div className="lg:col-span-8 flex flex-col gap-2 h-full">
          {/* Skeleton do Banner de Insights da Base de Conhecimento */}
          <Card
            variant="glass"
            size="sm"
            className="relative overflow-hidden bg-linear-to-r from-purple-50/50 via-indigo-50/50 to-blue-50/50 border-purple-200! shadow-xs [&>div]:flex [&>div]:flex-col [&>div]:gap-3 w-full shrink-0"
          >
            <div className="relative z-10">
              {/* Header do Banner */}
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex items-center gap-2.5">
                  {/* Ícone */}
                  <div className="w-9 h-9 bg-purple-100 rounded-lg animate-pulse shrink-0" />
                  <div>
                    {/* Subtitle uppercase */}
                    <div className="h-3 w-28 bg-purple-200 rounded-sm animate-pulse mb-1.5" />
                    {/* Title */}
                    <div className="h-5 w-48 bg-gray-200 rounded-sm animate-pulse" />
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div className="space-y-1.5 mb-3">
                <div className="h-3 w-full bg-gray-200/65 rounded-sm animate-pulse" />
                <div className="h-3 w-3/4 bg-gray-200/65 rounded-sm animate-pulse" />
              </div>

              {/* Cards de Insights de IA */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="bg-white/75 backdrop-blur-xs rounded-xl p-3 border border-purple-200 flex gap-2.5 items-start">
                  <div className="w-6 h-6 rounded-lg bg-blue-100/50 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-full bg-gray-200/50 rounded-sm animate-pulse" />
                    <div className="h-3 w-5/6 bg-gray-200/50 rounded-sm animate-pulse" />
                  </div>
                </div>

                <div className="bg-white/75 backdrop-blur-xs rounded-xl p-3 border border-purple-200 flex gap-2.5 items-start">
                  <div className="w-6 h-6 rounded-lg bg-purple-100/50 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-full bg-gray-200/50 rounded-sm animate-pulse" />
                    <div className="h-3 w-2/3 bg-gray-200/50 rounded-sm animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Skeleton de Orientação de Mercado */}
          <Card
            variant="default"
            size="sm"
            className="border-purple-200! flex-1 [&>div]:h-full [&>div]:flex [&>div]:flex-col [&>div]:justify-between"
          >
            <div className="flex flex-col h-full justify-between">
              {/* Header de Seção */}
              <div className="flex items-center gap-2.5 border-b border-purple-100 pb-2 mb-3.5 shrink-0">
                <div className="w-9 h-9 bg-emerald-50 rounded-lg animate-pulse shrink-0" />
                <div>
                  <div className="h-3 w-24 bg-gray-100 rounded-sm animate-pulse mb-1.5" />
                  <div className="h-4.5 w-36 bg-gray-200 rounded-sm animate-pulse" />
                </div>
              </div>

              {/* Grid de Segmentos */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1 items-stretch">
                {[...Array(3)].map((_, idx) => (
                  <div
                    key={idx}
                    className="relative rounded-xl border border-purple-200 bg-purple-50/10 p-3.5 flex flex-col justify-between h-full min-h-[120px]"
                  >
                    <div className="flex-1 flex flex-col">
                      {/* Badge / Index */}
                      <div className="flex items-center justify-between">
                        <div className="h-4.5 w-20 bg-gray-200/50 rounded-full animate-pulse" />
                        <div className="h-3.5 w-5 bg-gray-200/50 rounded-sm animate-pulse" />
                      </div>

                      {/* Value / Label */}
                      <div className="mt-3">
                        <div className="h-7 w-12 bg-gray-300 rounded-sm animate-pulse" />
                        <div className="h-3 w-24 bg-gray-200/50 rounded-sm animate-pulse mt-1.5" />
                      </div>

                      {/* Mini-métricas (% do total / novos 7d) */}
                      <div className="mt-3 grid grid-cols-2 gap-1.5">
                        <div className="rounded-lg bg-white/70 border border-purple-100 px-2 py-1.5 space-y-1">
                          <div className="h-3.5 w-6 bg-gray-300 rounded-sm animate-pulse" />
                          <div className="h-2 w-10 bg-gray-200/50 rounded-sm animate-pulse" />
                        </div>
                        <div className="rounded-lg bg-white/70 border border-purple-100 px-2 py-1.5 space-y-1">
                          <div className="h-3.5 w-6 bg-gray-300 rounded-sm animate-pulse" />
                          <div className="h-2 w-10 bg-gray-200/50 rounded-sm animate-pulse" />
                        </div>
                      </div>

                      {/* Progress dots bar */}
                      <div className="mt-auto pt-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <div
                              key={i}
                              className="h-1.5 flex-1 rounded-full bg-gray-200/60 animate-pulse"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Coluna Direita: Painel de Ativos da Biblioteca */}
        <div className="lg:col-span-4 h-full">
          <Card
            variant="default"
            size="sm"
            className="border-purple-200! h-full [&>div]:h-full [&>div]:flex [&>div]:flex-col [&>div]:justify-between [&>div]:gap-4.5"
          >
            {/* Header de Ativos */}
            <div className="flex items-center justify-between border-b border-purple-100 pb-2">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-purple-50 rounded-lg animate-pulse shrink-0" />
                <div>
                  <div className="h-3 w-20 bg-gray-100 rounded-sm animate-pulse mb-1.5" />
                  <div className="h-4.5 w-32 bg-gray-200 rounded-sm animate-pulse" />
                </div>
              </div>
              <div className="h-5 w-28 bg-purple-100/50 rounded-full animate-pulse" />
            </div>

            {/* Mini KPIs de Volume */}
            <div className="grid grid-cols-2 gap-2 pb-2 border-b border-purple-100">
              <div className="bg-blue-50/20 border border-purple-200 rounded-lg p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100/50 animate-pulse shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-2.5 w-10 bg-gray-100 rounded-sm animate-pulse" />
                  <div className="h-5 w-12 bg-gray-300 rounded-sm animate-pulse" />
                </div>
              </div>

              <div className="bg-emerald-50/20 border border-purple-200 rounded-lg p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100/50 animate-pulse shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-2.5 w-14 bg-gray-100 rounded-sm animate-pulse" />
                  <div className="h-5 w-8 bg-gray-300 rounded-sm animate-pulse" />
                </div>
              </div>
            </div>

            {/* Categorias de Conteúdo */}
            <div className="space-y-2 flex-1 flex flex-col justify-between">
              {/* Category distribution header */}
              <div className="h-3.5 w-28 bg-gray-200 rounded-sm animate-pulse shrink-0" />

              <div className="space-y-3 flex-1 flex flex-col justify-between">
                {[...Array(4)].map((_, idx) => (
                  <div
                    key={idx}
                    className="bg-purple-50/5 border border-purple-200 rounded-xl p-2.5 flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-purple-100/30 animate-pulse shrink-0" />
                        <div className="h-3.5 w-16 bg-gray-200/50 rounded-sm animate-pulse" />
                      </div>
                      <div className="h-3.5 w-6 bg-gray-300 rounded-sm animate-pulse" />
                    </div>

                    {/* H Progress Bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full animate-pulse" />
                      <div className="h-3 w-8 bg-gray-200/50 rounded-sm animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContentAnalyticsSkeleton;
